import { useEffect, useState, useRef } from 'react';
import { useBreakpoint, Breakpoints, useTranslation, FAIcon, IconStyle } from '@nextgen-web-framework/all';
import Facets from '../Facets/Facets';
import PageFilter, { getBadge } from '../PageFilter/PageFilter';
import ProductCardItems from '../ProductCardItems/ProductCardItems';
import EmptyResult from '../EmptyResult/EmptyResult';
import ProductCardLoaders from '../ProductCardLoaders/ProductCardLoaders';
import { ProductResultsProps } from './types/propTypes';
import styles from './ProductResults.module.scss';

const ProductResults = ({
  searchedKeyword,
  plpType,
  isPLP,
  noResultFilterName,
  limit,
  isLoading,
  fulltextSearchResults,
  facets,
  filter,
  availableQuickFilters,
  productsData,
  searchedTextHasData,
  badgeCount,
  priceRangeInterval,
  categoryAndSolutionDetails,
  showDesktopFacets,
  goToShop,
  setLastProductCard,
  setShowDesktopFacets,
  updateFilter,
  updateQuickFilter,
  resetFilter,
}: ProductResultsProps) => {
  const [showFacetsDrawer, setShowFacetsDrawer] = useState(false);
  const productListingRef = useRef<HTMLDivElement>(null);
  const viewPortType = useBreakpoint() as Breakpoints;
  const t = useTranslation();
  const { XS, SM, LG, XL } = Breakpoints;

  useEffect(() => {
    setShowDesktopFacets(false);
    setShowFacetsDrawer(false);
  }, [viewPortType, searchedKeyword]);

  const toggleSortAndFilter = () => {
    // for Desktop: toggle inline facets, for Tablet: open facets drawer
    if ([LG, XL].includes(viewPortType)) {
      setShowDesktopFacets(!showDesktopFacets);
    } else {
      setShowFacetsDrawer(true);
    }
  };

  const scrollToProductListingDiv = () => {
    if (productListingRef.current) {
      productListingRef.current.scrollIntoView({ behavior: 'smooth' }); // 'smooth' for animated scroll
    }
  };

  const facetsComponent = (
    <Facets
      setShowFacetsDrawer={setShowFacetsDrawer}
      facets={facets}
      filter={filter}
      plpType={plpType}
      categoryAndSolutionDetails={categoryAndSolutionDetails}
      priceRangeInterval={priceRangeInterval}
      noResultFilterName={noResultFilterName}
      updateFilter={updateFilter}
      resetFilter={resetFilter}
      scrollTo={scrollToProductListingDiv}
    />
  );

  return (
    <div className={styles['product-results']} ref={productListingRef}>
      {(searchedTextHasData || (fulltextSearchResults?.total as number) > 0) && (
        <PageFilter
          availableQuickFilters={availableQuickFilters}
          badgeCount={badgeCount}
          isSortAndFilterButtonActive={showDesktopFacets || showFacetsDrawer}
          isPLP={isPLP}
          toggleSortAndFilter={toggleSortAndFilter}
          updateQuickFilter={updateQuickFilter}
        />
      )}
      {Object.keys(fulltextSearchResults).length > 0 ? (
        <>
          {searchedTextHasData || fulltextSearchResults?.total ? (
            <div className={styles['result-body-container']}>
              {[LG, XL].includes(viewPortType) && showDesktopFacets && (
                <div className={styles['desktop-facets']}>{facetsComponent}</div>
              )}
              <div className={styles['result-body']}>
                <div className={styles['search-result-header']}>
                  <div className={styles['result-count']}>
                    {t('search.searchResult.results')} ({fulltextSearchResults?.total})
                  </div>
                  {[XS, SM].includes(viewPortType) && (
                    <div className={styles['facets-icon-container']}>
                      <div className={styles['icon-container']}>
                        <FAIcon
                          iconName="sliders"
                          iconStyle={IconStyle.REGULAR}
                          data-testid="mobile-facets-icon"
                          className={styles.icon}
                          onClick={() => setShowFacetsDrawer(true)}
                        />
                        {getBadge(badgeCount)}
                      </div>
                    </div>
                  )}
                </div>
                <ProductCardItems
                  products={productsData || []}
                  setLastProductCard={setLastProductCard}
                  loading={isLoading}
                  limit={limit}
                  showDesktopFacets={showDesktopFacets}
                />
                {!isLoading && fulltextSearchResults?.total === 0 && (
                  <EmptyResult
                    noResultLabels={{
                      emptyResultTxt1: t('search.searchResult.emptyResultForFilters'),
                      emptyResultTxt2: t('search.searchResult.emptyResultSuggestionForFilters'),
                    }}
                    goToShop={goToShop}
                  />
                )}
              </div>
              {showFacetsDrawer && facetsComponent}
            </div>
          ) : (
            <EmptyResult
              noResultLabels={{
                emptyResultTxt1: t('search.searchResult.emptyResultForKeyword').replace(
                  '{{keyword}}',
                  `“${searchedKeyword}”`,
                ),
                emptyResultTxt2: t('search.searchResult.emptyResultSuggestionForKeyword'),
                emptyResultBtnLabel: t('search.searchResult.searchEmptyResultBtnLabel'),
              }}
              goToShop={goToShop}
            />
          )}
        </>
      ) : (
        <ul className={styles['initial-result-loading-container']}>
          <ProductCardLoaders limit={limit} />
        </ul>
      )}
    </div>
  );
};

export default ProductResults;
